import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  buildLiveDashboardRedirectUrl,
  clearLiveDashboardSession,
  enableFullPermissionPreview,
  liveDashboardLogin,
} from '../services/liveDashboardAuthService';
import { getApiUrl } from '../utils/api';
import { isEmbedded, notifyParentSessionExpired } from '../utils/embed';

const getTokenParam = (key: string): string => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const queryParams = new URLSearchParams(window.location.search);
  return (hashParams.get(key) || queryParams.get(key) || '').trim();
};

const LiveLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authToken, setAuthToken] = useState(getTokenParam('authToken') || getTokenParam('accessToken'));
  const [refreshToken, setRefreshToken] = useState(getTokenParam('refreshToken'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const endpoint = useMemo(
    () => getApiUrl('/v1/embed-auth/live-login'),
    [],
  );

  const submitLiveLogin = async (
    tokenOverride?: { authToken?: string; refreshToken?: string },
  ) => {
    const nextAuthToken = (tokenOverride?.authToken ?? authToken).trim();
    const nextRefreshToken = (tokenOverride?.refreshToken ?? refreshToken).trim();

    if (!nextAuthToken) {
      setError('authToken is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const result = await liveDashboardLogin({
        authToken: nextAuthToken,
        refreshToken: nextRefreshToken || undefined,
      });
      if ('frontendUrl' in result && result.frontendUrl) {
        window.location.assign(buildLiveDashboardRedirectUrl(result.frontendUrl, {
          authToken: nextAuthToken,
          refreshToken: nextRefreshToken || undefined,
        }));
        return;
      }
      navigate('/home', { replace: true });
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Live login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startLocalPreview = () => {
    clearLiveDashboardSession();
    enableFullPermissionPreview();
    navigate('/home', { replace: true });
  };

  const embedded = isEmbedded();
  const hasHandoffTokens = Boolean(getTokenParam('authToken') || getTokenParam('accessToken'));
  // The manual paste-token form is a developer tool. Never show it to an
  // embedded end-user - only standalone, or when a developer explicitly asks
  // for it with ?dev=true.
  const showDevForm = !embedded || searchParams.get('dev') === 'true';
  const isReconnecting = isSubmitting || hasHandoffTokens;

  useEffect(() => {
    const handoffAuthToken = getTokenParam('authToken') || getTokenParam('accessToken');
    const handoffRefreshToken = getTokenParam('refreshToken');
    if (handoffAuthToken || (searchParams.get('autoSubmit') === 'true' && authToken.trim())) {
      submitLiveLogin({
        authToken: handoffAuthToken || authToken,
        refreshToken: handoffRefreshToken || refreshToken,
      });
    } else if (embedded) {
      // Embedded with no handoff token = a session that expired/was lost.
      // Ask the parent Rage app to re-authenticate; it can re-handoff a token.
      notifyParentSessionExpired();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showDevForm) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-slate-900">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
          {isReconnecting ? (
            <>
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
              <h1 className="text-lg font-bold">Connecting to your dashboard…</h1>
              <p className="mt-2 text-sm text-slate-500">One moment while we verify your session.</p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-bold text-slate-900">Your session has expired</h1>
              <p className="mt-2 text-sm text-slate-500">
                Please reload the page to continue using the dashboard.
              </p>
              <button
                className="mt-5 rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
                onClick={() => window.location.reload()}
                type="button"
              >
                Reload
              </button>
            </>
          )}
          {error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-10 text-slate-900">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-3xl items-center justify-center">
        <div className="w-full rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-blue-600">
              AI Dashboard
            </div>
            <h1 className="mb-2 text-2xl font-bold">Live Rage Token Login</h1>
            <p className="text-sm text-slate-600">
              Paste the Live Rage tokens to call the AI Dashboard live-login endpoint and verify screen permissions.
            </p>
          </div>

          <div className="mb-5 rounded-md border border-slate-200 bg-slate-50 p-3 text-left text-xs text-slate-600">
            <div className="font-semibold text-slate-800">POST endpoint</div>
            <div className="mt-1 break-all font-mono">{endpoint}</div>
          </div>

          <div className="space-y-4">
            <label className="block text-left">
              <span className="mb-1 block text-sm font-semibold text-slate-700">authToken</span>
              <textarea
                className="min-h-32 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                value={authToken}
                onChange={(event) => setAuthToken(event.target.value)}
                placeholder="Paste authToken..."
              />
            </label>

            <label className="block text-left">
              <span className="mb-1 block text-sm font-semibold text-slate-700">refreshToken</span>
              <textarea
                className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                value={refreshToken}
                onChange={(event) => setRefreshToken(event.target.value)}
                placeholder="Paste refreshToken..."
              />
            </label>
          </div>

          {error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-left text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              className="rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
              onClick={startLocalPreview}
              type="button"
            >
              Preview All 24 Screens Locally
            </button>
            <button
              className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
              onClick={() => submitLiveLogin()}
              type="button"
            >
              {isSubmitting ? 'Validating...' : 'Connect to Dashboard'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveLoginPage;
